import { httpsCallable } from "firebase/functions"

import { functions } from "../firebaseFunctions"

export const REFERRAL_QUERY_PARAM = "ref"
export const REFERRAL_STORAGE_KEY = "winfacil_referral_code"

export type RedeemReferralResult = {
  referredBy: string
  alreadyRedeemed: boolean
}

const redeemReferralCallable = httpsCallable<{ code: string }, RedeemReferralResult>(
  functions,
  "redeemReferral",
)

export const redeemReferral = async (code: string): Promise<RedeemReferralResult> => {
  const result = await redeemReferralCallable({ code })
  return result.data
}

export const getReferralLink = (uid: string, origin: string = window.location.origin): string =>
  `${origin.replace(/\/$/, "")}/?${REFERRAL_QUERY_PARAM}=${encodeURIComponent(uid)}`

// Kept as a pure function (URL string in, code out) so it's testable without a real
// `window.location`/localStorage. Called once at app bootstrap.
export const extractReferralCodeFromUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url)
    const code = parsed.searchParams.get(REFERRAL_QUERY_PARAM)
    return code && code.trim() ? code.trim() : null
  } catch {
    return null
  }
}

// Captures `?ref=<uid>` into localStorage on first visit so it survives the redirect through
// sign-up/login, then strips it from the visible URL. Never overwrites an already-stored code
// — the first link a visitor followed should get credit, not the last one they happen to
// click before finishing sign-up.
export const captureReferralCodeFromLocation = (): void => {
  if (typeof window === "undefined") {
    return
  }

  const code = extractReferralCodeFromUrl(window.location.href)
  if (!code) {
    return
  }

  try {
    if (!window.localStorage.getItem(REFERRAL_STORAGE_KEY)) {
      window.localStorage.setItem(REFERRAL_STORAGE_KEY, code)
    }
  } catch {
    // localStorage unavailable (private browsing, etc.) — nothing to persist, safe to ignore.
  }

  const url = new URL(window.location.href)
  url.searchParams.delete(REFERRAL_QUERY_PARAM)
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`)
}

export const consumeStoredReferralCode = (): string | null => {
  if (typeof window === "undefined") {
    return null
  }

  try {
    const code = window.localStorage.getItem(REFERRAL_STORAGE_KEY)
    if (code) {
      window.localStorage.removeItem(REFERRAL_STORAGE_KEY)
    }
    return code
  } catch {
    return null
  }
}
